import React from 'react';

/**
 * ⛔⛔ THE TEMPORARY PASSWORD, SHOWN SO IT CAN BE TYPED CORRECTLY FIRST TIME —
 * Card TEMP-PASSWORD-CLARITY, 7 Oct 2026.
 *
 * ── WHAT WENT WRONG, MEASURED ON THE LIVE GO-LIVE TEST (6–7 Oct) ───────────
 *
 * The reset worked — Cognito updated at 01:26:19Z — and Daniel then failed
 * FIVE OR MORE sign-ins with "incorrect credentials". He typed the password in
 * all lowercase, and his phone keyboard lowercased it for him.
 *
 * This component was why. It rendered `Beach · Tiger · 4827` and a reader could
 * not tell:
 *   (a) whether the capitals mattered,
 *   (b) whether the dots were part of the password,
 *   (c) whether there were spaces, dashes or underscores,
 *   (d) whether it was one continuous string at all.
 *
 * ⚠⚠ THE SEPARATORS WERE A DISPLAY CHOICE THAT READ AS DATA. They were
 * `aria-hidden` and documented as decoration, and that documentation was true
 * and useless: the person reading the screen is not reading the source. Nothing
 * on screen said "no spaces", and nothing said the capitals were load-bearing.
 *
 * ⇒ THE DOTS ARE GONE. The value is rendered as ONE CONTIGUOUS STRING, and the
 * component now says out loud what shape it is and how to say it.
 *
 * ── ⛔ WHAT IS STILL FORBIDDEN ─────────────────────────────────────────────
 *
 * NO COPY BUTTON AND NO navigator.clipboard — Daniel's ruling P35 item 2,
 * 3 Oct 2026. The clipboard outlives the panel, survives "Done", and is
 * captured by every clipboard-history tool. The password is READ ALOUD; that is
 * the whole workflow. A control arm asserts this file contains no clipboard
 * call.
 *
 * ⚠ The generator's alphabet and the Cognito password policy are NOT touched by
 * this card. This is a legibility fix, not a credential-format change.
 */

/**
 * ⚠ MIRRORS database/partnerCredential.js's TEMP_PASSWORD_RE deliberately.
 *
 * It is copied rather than imported because Create React App's module scope
 * forbids importing from outside src/, and database/ is server code. The two
 * literals are held together by a source-level assertion in
 * tests/unit/security/partnerTempPasswordFormat.test.js, which reads BOTH
 * files and compares the patterns — a comment saying "keep these in sync" is
 * not a control.
 */
export const TEMP_PASSWORD_RE = /^([A-Z][a-z]{3,5})([A-Z][a-z]{3,5})([2-9]{4})$/;

/** `"BlueRiver4827"` -> `["Blue", "River", "4827"]`, or `null`. */
export function chunks(password) {
  const m = TEMP_PASSWORD_RE.exec(String(password || ''));
  return m ? [m[1], m[2], m[3]] : null;
}

/**
 * How a person says one part out loud.
 *
 * ⚠⚠ DERIVED FROM THE VALUE, NEVER HARD-CODED. An example baked into the copy
 * would be read aloud verbatim by a tired staff member at 5pm on a Friday —
 * which is exactly the class of mistake this card exists to stop.
 *
 *   "Blue"  -> { capital: 'B', rest: ['l','u','e'] }
 *   "4827"  -> { digits: ['4','8','2','7'] }
 */
export function spokenParts(password) {
  const parts = chunks(password);
  if (!parts) return null;
  const [a, b, digits] = parts;
  return [
    { kind: 'word', capital: a[0], rest: a.slice(1).split('') },
    { kind: 'word', capital: b[0], rest: b.slice(1).split('') },
    { kind: 'digits', digits: digits.split('') },
  ];
}

/**
 * The single string a screen reader should hear.
 *
 * ⛔ SPELLED, NOT SPOKEN AS WORDS. "Blue River four thousand eight hundred and
 * twenty-seven" is unusable for typing. Commas and full stops are pauses to a
 * screen reader, not characters, so they shape the rhythm without ever being
 * announced as punctuation — which is the one thing the old middots did wrong.
 */
export function spokenText(password) {
  const spoken = spokenParts(password);
  if (!spoken) return '';
  return spoken.map((p) => (p.kind === 'word'
    ? `Capital ${p.capital}, ${p.rest.join(', ')}.`
    : p.digits.join(', ')))
    .join(' ');
}

const CAP = { fontWeight: 800, textDecoration: 'underline', textUnderlineOffset: 3 };

export default function TempPasswordReadout({ password, testId = 'temp-password' }) {
  const value = String(password == null ? '' : password);
  const spoken = spokenParts(value);

  return (
    <div>
      {/*
        ⛔⛔ ONE CONTIGUOUS STRING. No separator characters of any kind — not a
        middot, not a space, not a zero-width anything. `letterSpacing` is CSS,
        so it widens the glyphs without putting a character between them: a
        reader cannot mistake the gap for something they must type, and a
        selection copies the real value.
      */}
      <div
        data-testid={testId}
        style={{
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
          fontSize: 30,
          lineHeight: 1.25,
          fontWeight: 700,
          letterSpacing: '0.08em',
          color: '#212529',
          userSelect: 'all',
          wordBreak: 'break-all',
          padding: '10px 12px',
          background: '#fff',
          border: '1px solid #F0B429',
          borderRadius: 4,
        }}
      >
        {/*
          ⚠ The visible string is aria-hidden and the SPELLED version carries
          the accessible name. A screen reader announcing "BlueRiver4827" as a
          word is unusable for typing; announcing it letter by letter, with the
          capitals called out, is the whole point.
        */}
        <span aria-hidden="true">{value}</span>
        <span
          data-testid={`${testId}-spoken`}
          style={{
            position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
            overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
          }}
        >
          {spoken ? spokenText(value) : value.split('').join(', ')}
        </span>
      </div>

      {/*
        ⛔ THE RULE LINE, AND THE COUNT IS COMPUTED. A hard-coded "12
        characters" would quietly become a lie the first time the generator's
        word lengths changed — the regex allows 12 to 16.
      */}
      <div data-testid={`${testId}-rule`} style={{ marginTop: 8, fontSize: 12, fontWeight: 600, color: '#8A6100' }}>
        Type it as one word: {value.length} characters, no spaces, dots, dashes or
        underscores. Capital letters matter.
      </div>

      {spoken ? (
        <div data-testid={`${testId}-breakdown`} style={{ marginTop: 8, fontSize: 12, color: '#495057' }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>How to read it aloud</div>
          <ol style={{ margin: 0, paddingLeft: 20, lineHeight: 1.6 }}>
            {spoken.map((p, i) => (
              <li key={`${p.kind}-${i}`}>
                {p.kind === 'word' ? (
                  <>
                    {/*
                      ⛔ EMPHASIS IS BOLD **AND** UNDERLINE, NEVER COLOUR ALONE.
                      Colour alone fails WCAG 1.4.1, and this is the single
                      detail that caused five failed sign-ins — it has to
                      survive a monochrome screen and a colour-blind reader.
                    */}
                    Capital <span style={CAP}>{p.capital}</span>, then{' '}
                    <span style={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>
                      {p.rest.join('-')}
                    </span>{' '}
                    → &ldquo;<span style={{ fontWeight: 700 }}>{p.capital}{p.rest.join('')}</span>&rdquo;
                  </>
                ) : (
                  <>Numbers: <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 700 }}>{p.digits.join(' ')}</span></>
                )}
              </li>
            ))}
          </ol>
        </div>
      ) : null}

      {/*
        ⚠ THE INSTRUCTION NAMES THE SHAPE. "Read it to them" was true and did
        not say what "it" looked like; a partner who hears two words and four
        numbers needs to know they are joined and capitalised.
      */}
      <div data-testid={`${testId}-instruction`} style={{ marginTop: 8, fontSize: 12, color: '#8A6100' }}>
        Two words, each starting with a capital letter, then four numbers, all joined
        together. It works once; they choose their own at first sign-in.
      </div>
    </div>
  );
}
